import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Send } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const { threadKey } = useParams();
  const [threads, setThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);

  const loadThreads = async () => {
    const { data } = await api.get("/messages/threads");
    setThreads(data);
    setLoading(false);
  };

  const loadThread = async () => {
    if (!threadKey) { setMessages([]); return; }
    const { data } = await api.get(`/messages/thread/${threadKey}`);
    setMessages(data);
  };

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { loadThread(); }, [threadKey]);

  const activeThread = threads.find((t) => t.thread_key === threadKey);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || !activeThread) return;
    try {
      await api.post("/messages", { to_user_id: activeThread.other_user_id, body, thread_key: threadKey });
      setBody("");
      loadThread();
      loadThreads();
    } catch (err) {
      toast.error("Send failed");
    }
  };

  return (
    <div className="min-h-screen bg-appbg text-white">
      <Header />
      <div className="px-4 md:px-8 lg:px-12 py-8 max-w-6xl" data-testid="messages-page">
        <div className="text-xs tracking-widest text-volt">SECURE IN-APP</div>
        <h1 className="heading text-4xl md:text-5xl mt-2">MESSAGES</h1>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 min-h-[500px]">
          <aside className="border border-neutral-800 bg-appcard md:col-span-1" data-testid="thread-list">
            <div className="px-4 py-3 border-b border-neutral-800 text-xs tracking-widest text-neutral-500">THREADS</div>
            {loading ? <div className="p-4 text-neutral-500">Loading…</div> : threads.length === 0 ? (
              <div className="p-4 text-neutral-500 text-sm">No conversations yet.</div>
            ) : threads.map((t) => (
              <Link
                key={t.thread_key}
                to={`/messages/${t.thread_key}`}
                data-testid={`thread-${t.thread_key}`}
                className={`block px-4 py-3 border-b border-neutral-800 hover:bg-[#121214] transition-colors ${threadKey === t.thread_key ? "bg-[#121214] border-l-2 border-l-blaze" : ""}`}
              >
                <div className="heading text-base">{t.other_name}</div>
                <div className="text-neutral-400 text-xs truncate">{t.last_body}</div>
                <div className="text-mono text-[10px] text-neutral-500 mt-1">{new Date(t.last_at).toLocaleString()}</div>
              </Link>
            ))}
          </aside>

          <section className="md:col-span-2 border border-neutral-800 bg-appcard flex flex-col">
            {!activeThread ? (
              <div className="flex-1 flex items-center justify-center text-neutral-500 p-10">Select a thread to view messages.</div>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-neutral-800">
                  <div className="heading text-xl">{activeThread.other_name}</div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="thread-messages">
                  {messages.map((m) => {
                    const me = m.from_user_id === user.id;
                    return (
                      <div key={m.id} className={`flex ${me ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] p-3 ${me ? "bg-blaze text-white" : "bg-[#121214] text-neutral-200"}`}>
                          <div className="text-sm whitespace-pre-line">{m.body}</div>
                          <div className="text-[10px] text-mono opacity-70 mt-1">{new Date(m.created_at).toLocaleString()}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <form onSubmit={send} className="border-t border-neutral-800 p-3 flex gap-2">
                  <input className="flex-1 bg-[#121214] border border-neutral-800 px-3 py-2 outline-none text-sm" data-testid="message-input" placeholder="Type a message…" value={body} onChange={(e) => setBody(e.target.value)} />
                  <button type="submit" data-testid="send-message-btn" className="bg-blaze text-white px-4 hover:glow-blaze transition-all"><Send size={16} /></button>
                </form>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
